import { useEffect, useState } from 'react';

// Global flag to track loading state across all components
let isLoading = false;
let isLoadedGlobal = false;

const useGoogleMaps = (apiKey) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    // If already loaded globally, just set state
    if (isLoadedGlobal && window.google?.maps?.places) {
      setIsLoaded(true);
      return;
    }

    // If currently loading, wait for it
    if (isLoading) {
      const checkInterval = setInterval(() => {
        if (window.google?.maps?.places) {
          setIsLoaded(true);
          isLoadedGlobal = true;
          clearInterval(checkInterval);
        }
      }, 100);

      return () => clearInterval(checkInterval);
    }

    // Check if script already exists in DOM
    const existingScript = document.querySelector(
      'script[src*="maps.googleapis.com/maps/api/js"]'
    );
    
    if (existingScript) {
      // Script exists, wait for it to load
      if (window.google?.maps?.places) {
        setIsLoaded(true);
        isLoadedGlobal = true;
      } else {
        existingScript.addEventListener('load', () => {
          setIsLoaded(true);
          isLoadedGlobal = true;
        });
      }
      return;
    }

    // No script exists, create it
    if (!apiKey) {
      setLoadError('Google Maps API key is missing');
      return;
    }

    isLoading = true;

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script'; // Add ID for easy identification

    script.onload = () => {
      if (window.google?.maps?.places) {
        setIsLoaded(true);
        isLoadedGlobal = true;
        isLoading = false;
      } else {
        setLoadError('Google Maps loaded but Places library not available');
        isLoading = false;
      }
    };

    script.onerror = () => {
      setLoadError('Failed to load Google Maps. Check your API key and internet connection.');
      isLoading = false;
    };

    document.head.appendChild(script);

    // Cleanup
    return () => {
      // Don't remove script on unmount to prevent reloading
      // The script should stay loaded for the entire app lifetime
    };
  }, [apiKey]);

  return { isLoaded, loadError };
};

export default useGoogleMaps;